/* Входы формы «Коллажа из фото» ⇄ состояние движка шаблона (план вехи 3, шаг 2; collage-slides §2.1). Чистые функции без DOM:
   viewOf — что показать в S по входам сервиса (повтор из «Истории», «Изменить», заявка родной формы), inputsOf — входы из S (зеркало
   inputs-mirror, точки записи — syncFoot и pvPaint движка). Ключи и кодирование — те же, что пишет родная форма (CollageOrderForm.tsx):
   раскладка (layout — значение сервера grid_2x2…, collage-renderer.js IDS), формат (ratio), отступы (gap none|thin|medium|wide), фон
   (background white|black|paper|photo|blur), подпись (caption). Контрактные имена экрана (layout_id, format, gap 0|s|m|l, bg …|tone|…)
   принимаются так же: значение пишется так, как его публикует поле (id шаблона — как есть, иначе по таблице). Фото пишет мост загрузок
   (media-bridge: слоты photo_01…photo_09 по порядку записей или JSON-список photo_upload_ids) — inputsOf их не трогает. */
import type { StudioForm } from '../studio/studio-api';
import type { TraySpec } from '../studio/engine-kit/media-bridge';
import { collageLayouts } from './collage-renderer.js';

type Field = StudioForm['fields'][number];

/** Поля формы экрана: выборы (первое найденное имя), подпись, слоты фото или JSON-список фото */
export interface CollageFields {
  layout?: Field;
  ratio?: Field;
  gap?: Field;
  bg?: Field;
  caption?: Field;
  /** Слоты фото формы (photo_01…, по номеру) */
  photos: Field[];
  /** JSON-список фото контракта (photo_upload_ids) */
  list?: Field;
}
const KEYS = {
  layout: ['layout', 'layout_id'],
  ratio: ['ratio', 'format'],
  gap: ['gap'],
  bg: ['background', 'bg'],
  caption: ['caption'],
} as const;
const PHOTO_SLOT = /^photo_(\d+)$/;
const LIST_KEY = 'photo_upload_ids';
const slotNo = (key: string) => Number(key.match(/(\d+)$/)?.[1] ?? 0);
const field = (form: StudioForm | null | undefined, keys: readonly string[]) => keys.map(key => form?.fields.find(item => item.key === key)).find(Boolean);

export function collageFields(form: StudioForm | null | undefined): CollageFields {
  const photos = (form?.fields ?? [])
    .filter(item => PHOTO_SLOT.test(item.key) && item.upload?.kind === 'photo')
    .sort((a, b) => slotNo(a.key) - slotNo(b.key));
  return {
    layout: field(form, KEYS.layout),
    ratio: field(form, KEYS.ratio),
    gap: field(form, KEYS.gap),
    bg: field(form, KEYS.bg),
    caption: field(form, KEYS.caption),
    photos,
    list: photos.length ? undefined : form?.fields.find(item => item.key === LIST_KEY),
  };
}
/** Лоток фото моста загрузок: слоты формы по порядку или JSON-список (предел — constraints.max_photos, по умолчанию 9); null — фото не публикуются */
export function collageTray(form: StudioForm | null | undefined): TraySpec | null {
  const f = collageFields(form);
  if (f.photos.length) return f.photos.map(item => item.key);
  if (f.list) return { list: f.list.key, max: form?.constraints?.max_photos || 9 };
  return null;
}
/** Ключ политики загрузки фото (слот формы или ключ списка) */
export const photoPolicyKey = (form: StudioForm | null | undefined) => {
  const f = collageFields(form);
  return f.photos[0]?.key ?? f.list?.key ?? null;
};
/** Поддержан ли контракт: фото публикуются, а каждое обязательное поле — из тех, что экран умеет заполнить
    (неизвестное обязательное поле — «Этот вариант пока недоступен», как у товарных экранов) */
export function collageSupported(form: StudioForm | null | undefined): boolean {
  if (!form || !collageTray(form)) return false;
  const known = new Set<string>([...Object.values(KEYS).flat(), LIST_KEY]);
  return form.fields.every(item => !item.required || known.has(item.key) || (PHOTO_SLOT.test(item.key) && item.upload?.kind === 'photo'));
}

/* ---------- значения: id шаблона ⇄ значение формы ---------- */
/** Раскладка: id шаблона (g22) ⇄ значение сервера (grid_2x2) */
const LAYOUT = Object.fromEntries(collageLayouts.map(item => [item.id, item.value])) as Record<string, string>;
const GAP: Record<string, string> = { '0': 'none', s: 'thin', m: 'medium', l: 'wide' };
const BG: Record<string, string> = { tone: 'photo' };
const swap = (map: Record<string, string>) => Object.fromEntries(Object.entries(map).map(([a, b]) => [b, a])) as Record<string, string>;
const BACK = { layout: swap(LAYOUT), gap: swap(GAP), bg: swap(BG) };
/** Значение поля для id шаблона: опубликованное как есть, иначе по таблице; не опубликовано — undefined */
function valueOf(item: Field | undefined, id: string | undefined, map: Record<string, string>): string | undefined {
  if (!item || id === undefined) return undefined;
  if (item.values.includes(id)) return id;
  const mapped = map[id];
  return mapped !== undefined && item.values.includes(mapped) ? mapped : undefined;
}
/** id шаблона значения поля (или значения входа без формы): id шаблона — как есть, значение сервера — по таблице */
function idOf(value: string | undefined, map: Record<string, string>, back: Record<string, string>, ids: readonly string[]): string | undefined {
  if (value === undefined || value === '') return undefined;
  if (ids.includes(value) || Object.hasOwn(map, value)) return value;
  return Object.hasOwn(back, value) ? back[value] : undefined;
}
const LAYOUT_IDS = collageLayouts.map(item => item.id);
const RATIOS = ['1:1', '4:5', '3:4', '2:3', '9:16', '16:9'];
const GAP_IDS = Object.keys(GAP);
const BG_IDS = ['white', 'black', 'paper', 'tone', 'blur'];
/** id шаблона значений (для чтения повтора и тела заявки) */
export const layoutId = (value: string | undefined) => idOf(value, LAYOUT, BACK.layout, LAYOUT_IDS);
export const gapId = (value: string | undefined) => idOf(value, GAP, BACK.gap, GAP_IDS);
export const bgId = (value: string | undefined) => idOf(value, BG, BACK.bg, BG_IDS);
const ratioId = (value: string | undefined) => (value && RATIOS.includes(value) ? value : undefined);
/** id шаблона, которые публикует поле (порядок шаблона); поля нет — undefined (экран показывает все) */
export function publishedIds(item: Field | undefined, kind: 'layout' | 'ratio' | 'gap' | 'bg'): string[] | undefined {
  if (!item) return undefined;
  const ids = { layout: LAYOUT_IDS, ratio: RATIOS, gap: GAP_IDS, bg: BG_IDS }[kind];
  const map = { layout: LAYOUT, ratio: {}, gap: GAP, bg: BG }[kind];
  return ids.filter(id => valueOf(item, id, map) !== undefined);
}

/** Что экран держит в S: раскладка, формат, отступы, фон (id шаблона), подпись как есть */
export interface CollageView {
  lay?: string;
  ar?: string;
  gap?: string;
  bg?: string;
  text: string;
}

/** Входы → вид S (только опубликованные поля и значения; неопубликованное не задано) */
export function viewOf(form: StudioForm | null | undefined, inputs: Record<string, string>): CollageView {
  const f = collageFields(form);
  const pick = (item: Field | undefined) => {
    const value = item ? inputs[item.key] : undefined;
    return value !== undefined && item!.values.includes(value) ? value : undefined;
  };
  const view: CollageView = { text: f.caption ? (inputs[f.caption.key] ?? '') : '' };
  const lay = layoutId(pick(f.layout));
  if (lay) view.lay = lay;
  const ar = ratioId(pick(f.ratio));
  if (ar) view.ar = ar;
  const gap = gapId(pick(f.gap));
  if (gap) view.gap = gap;
  const bg = bgId(pick(f.bg));
  if (bg) view.bg = bg;
  return view;
}

/** Вид S → входы поверх prev: опубликованные поля экрана; фото — как в prev (их пишет мост). Пустая подпись, которой в prev
    не было, не появляется (как у родной формы: ключ пишет только правка) */
export function inputsOf(form: StudioForm | null | undefined, view: CollageView, prev: Record<string, string>): Record<string, string> {
  const f = collageFields(form);
  const next = { ...prev };
  const put = (item: Field | undefined, value: string | undefined) => {
    if (!item || value === undefined) return;
    if (value === '' && next[item.key] === undefined) return;
    next[item.key] = value;
  };
  put(f.layout, valueOf(f.layout, view.lay, LAYOUT));
  put(f.ratio, valueOf(f.ratio, view.ar, {}));
  put(f.gap, valueOf(f.gap, view.gap, GAP));
  put(f.bg, valueOf(f.bg, view.bg, BG));
  put(f.caption, view.text);
  return next;
}

/** Значения по умолчанию в опубликованные выборы, которых нет во входах: default_value поля, иначе — как у родной формы
    (mosaic, 1:1, medium, white), если поле его публикует. → [ключ, значение] */
export function collageDefaults(form: StudioForm | null | undefined, inputs: Record<string, string>): Array<[string, string]> {
  const f = collageFields(form);
  const out: Array<[string, string]> = [];
  const add = (item: Field | undefined, native: string, map: Record<string, string>) => {
    if (!item || inputs[item.key]) return;
    const own = typeof item.default_value === 'string' && item.values.includes(item.default_value) ? item.default_value : undefined;
    const value = own ?? valueOf(item, native, map);
    if (value) out.push([item.key, value]);
  };
  add(f.layout, 'mosaic', LAYOUT);
  add(f.ratio, '1:1', {});
  add(f.gap, 'm', GAP);
  add(f.bg, 'white', BG);
  return out;
}

/** Вход заказа → вид S без формы (заявка до прихода формы, хвост после перезагрузки): поля по допустимым именам ключей */
export function viewOfInputs(form: StudioForm | null | undefined, inputs: Record<string, unknown>): CollageView {
  const strings = Object.fromEntries(Object.entries(inputs).filter((entry): entry is [string, string] => typeof entry[1] === 'string'));
  if (form) return viewOf(form, strings);
  const first = (keys: readonly string[]) => keys.map(key => strings[key]).find(value => value !== undefined);
  const view: CollageView = { text: first(KEYS.caption) ?? '' };
  const lay = layoutId(first(KEYS.layout));
  if (lay) view.lay = lay;
  const ar = ratioId(first(KEYS.ratio));
  if (ar) view.ar = ar;
  const gap = gapId(first(KEYS.gap));
  if (gap) view.gap = gap;
  const bg = bgId(first(KEYS.bg));
  if (bg) view.bg = bg;
  return view;
}

/** Фото заказа: id загрузок слотов фото в порядке слотов (photo_01…photo_09) или из JSON-списка photo_upload_ids */
export function photoIds(form: StudioForm | null | undefined, inputs: Record<string, unknown>): string[] {
  const listed = inputs[LIST_KEY];
  if (typeof listed === 'string' || Array.isArray(listed)) {
    try {
      const ids: unknown = typeof listed === 'string' ? JSON.parse(listed) : listed;
      if (Array.isArray(ids)) return ids.filter((id): id is string => typeof id === 'string' && !!id);
    } catch {
      return [];
    }
  }
  const keys = form ? collageFields(form).photos.map(item => item.key) : Object.keys(inputs).filter(key => PHOTO_SLOT.test(key));
  return keys
    .slice()
    .sort((a, b) => slotNo(a) - slotNo(b))
    .map(key => inputs[key])
    .filter((id): id is string => typeof id === 'string' && !!id);
}
